/**
 * Parse the in-world panels before the session starts.
 *
 * A UIKitML document that fails to parse takes the whole panel with it, and
 * `PanelUISystem` reports that to `console.error` — a surface nobody can read
 * inside a headset. The user enters passthrough and finds no UI at all, with
 * nothing to distinguish a broken file from a rendering bug.
 *
 * So the panels are fetched and parsed here, on the flat page, where the
 * failure can be shown to the person holding the headset. `tests/panels.test.ts`
 * covers the authoring mistakes; this covers everything the tests cannot see —
 * a missing asset, a stale deploy, a proxy serving HTML for a 404.
 *
 * Advisory only. It never blocks entry: a broken bubble should not stop someone
 * looking at the guide, and a false negative here must not cost a demo.
 */
import { parseUIKitMLSource } from '@iwsdk/core';

import { uiLog } from './uiLog.js';

export interface PanelProblem {
  url: string;
  reason: string;
}

/** Check every panel URL. Resolves to the ones that would fail at runtime. */
export async function preflightPanels(urls: readonly string[]): Promise<PanelProblem[]> {
  const results = await Promise.all(urls.map(checkPanel));
  const problems = results.filter((result): result is PanelProblem => result !== null);

  if (problems.length === 0) {
    uiLog.push(`panels ok (${urls.length})`);
    return problems;
  }
  for (const { url, reason } of problems) {
    uiLog.push(`panel ${url}: ${reason}`);
  }
  return problems;
}

async function checkPanel(url: string): Promise<PanelProblem | null> {
  let source: string;
  try {
    const response = await fetch(url, { cache: 'no-cache' });
    if (!response.ok) return { url, reason: `HTTP ${response.status}` };
    source = await response.text();
  } catch (error) {
    return { url, reason: describe(error) };
  }

  try {
    // The same call `PanelUISystem` makes, with the same default kit, so a pass
    // here means a pass there.
    parseUIKitMLSource(source, new URL(url, document.baseURI).href);
    return null;
  } catch (error) {
    return { url, reason: firstLine(describe(error)) };
  }
}

/** Parse errors are one per line and the first is the one worth reading. */
function firstLine(message: string): string {
  const [first] = message.split('\n').filter((line) => line.trim().length > 0);
  return first ?? message;
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
